import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import {
  buttonColor,
  buttonHoverColor,
  darkPrimaryColor,
  fontFamilyall,
  primaryColor,
} from "../../UtilColorPalate/UtilColorPalate";
 import { LoginLogo } from "../../Data/image";
import { countryCodes } from "../../Data/data";
import { RegisterAPI } from "../../Utility/UtilApi/UtilApi";
import ForgotPasswordModal from "../ForgotPasswordModal/ForgotPasswordModal";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";


const RegisterPageModal = ({ open, onClose }) => {
  const initialFormData = {
    email: "",
    password: "",
    firstname: "",
    lastname: "",
    title: "",
    mobile: "",
    addresslineone: "",
    addresslinetwo: "",
    city: "",
    province: "",
    country: "",
    postalcode: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [toastmessage, setToastmessage] = useState("");
  
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [toastColor, setToastColor] = useState("");
  const [useEmail, setUseEmail] = useState(true);
  const [selectedCode, setSelectedCode] = useState("");

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (open) {
      setFormData(initialFormData);
      setError("");
      setUseEmail(true);
      setSelectedCode("");
      setLoading(false);
      setToastmessage("");
      setToastColor("");
      setSnackbarOpen(false);
    }
  }, [open]);

  const handleForgotPasswordClick = () => {
    setForgotPasswordOpen(true);
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
  };

  const handleCodeChange = (event) => {
    setSelectedCode(event.target.value);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSnackbarClick = () => {
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    // Validation with toast messages
    if (!formData.firstname) {
      setToastColor("error");
      setToastmessage("First Name is required!");
      handleSnackbarClick();
      return;
    }
    if (!formData.lastname) {
      setToastColor("error");
      setToastmessage("Last Name is required!");
      handleSnackbarClick();
      return;
    }
    if (!formData.title) {
      setToastColor("error");
      setToastmessage("Title is required!");
      handleSnackbarClick();
      return;
    }
    if (useEmail) {
      if (!formData.email) {
        setToastColor("error");
        setToastmessage("Email is required!");
        handleSnackbarClick();
        return;
      }
    } else {
      if (!selectedCode) {
        setToastColor("error");
        setToastmessage("Country Code is required!");
        handleSnackbarClick();
        return;
      }
      if (!formData.mobile) {
        setToastColor("error");
        setToastmessage("Mobile Number is required!");
        handleSnackbarClick();
        return;
      }
    }
    if (!formData.password) {
      setToastColor("error");
      setToastmessage("Password is required!");
      handleSnackbarClick();
      return;
    }
    if (!formData.addresslineone) {
      setToastColor("error");
      setToastmessage("Address Line One is required!");
      handleSnackbarClick();
      return;
    }
    if (!formData.city) {
      setToastColor("error");
      setToastmessage("City is required!");
      handleSnackbarClick();
      return;
    }
    if (!formData.country) {
      setToastColor("error");
      setToastmessage("Country is required!");
      handleSnackbarClick();
      return;
    }
    if (!formData.postalcode) {
      setToastColor("error");
      setToastmessage("Postal Code is required!");
      handleSnackbarClick();
      return;
    }

    setLoading(true);
    setError("");

    try {
      const signupPayload = {
        client: {
          name: `${formData.firstname} ${formData.lastname}`,
        },
        clientcontact: {
          ...(useEmail
            ? { email: formData.email }
            : { mobile: `${selectedCode}${formData.mobile}` }),
          password: formData.password,
          firstname: formData.firstname,
          lastname: formData.lastname,
          title: formData.title,
        },
        addresses: {
          name: formData.addresslineone,
          type: "Billing",
          addresslineone: formData.addresslineone,
          addresslinetwo: formData.addresslinetwo,
          city: formData.city,
          province: formData.province,
          postalcode: formData.postalcode,
          country: formData.country,
        },
      };

      console.log("Sending signup payload:", signupPayload);


      const response = await RegisterAPI(signupPayload);

      if (response.statusCode === 200 || response.statusCode === 201) {
        setToastColor("success");
        setToastmessage(response.message || "Registration successful!");
        handleSnackbarClick();
        setFormData(initialFormData);
        setError("");
        setUseEmail(true);
        setSelectedCode("");
        setTimeout(() => {
          navigate("/home");
          onClose();
        }, 2000);
      } else {
        setToastColor("error");
        setToastmessage(response.message || "Registration failed!");
        handleSnackbarClick();
      }
    } catch (error) {
      setToastColor("error");
      setToastmessage(
        error.response?.data?.message || "An error occurred during registration."
      );
      handleSnackbarClick();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? 300 : 500,
            bgcolor: "background.paper",
            borderRadius: 4,
            boxShadow: 6,
            p: isMobile ? 1 : 2,
            outline: "none",
            textAlign: "center",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 4,
              top: 4,
              color: "grey.500",
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={LoginLogo}
            style={{
              width: "40px",
              textAlign: "center",
              border: "none",
              boxShadow: "none",
            }}
            alt="Logo"
          />

          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight="bold"
            mb={1}
            sx={{ color: primaryColor, fontFamily : fontFamilyall}}
          >
            Register
          </Typography>

          <form onSubmit={handleSignup}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{fontFamily : fontFamilyall}}>
                    Title <span style={{ color: "red", }}>*</span>
                  </InputLabel>
                  <Select
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    label={
                      <>
                        Title <span style={{ color: "red" }}>*</span>
                      </>
                    }
                  >
                    <MenuItem value="Mr." sx={{fontFamily : fontFamilyall}}>Mr.</MenuItem>
                    <MenuItem value="Mrs." sx={{fontFamily : fontFamilyall}}>Mrs.</MenuItem>
                    <MenuItem value="Miss." sx={{fontFamily : fontFamilyall}}>Miss.</MenuItem>
                    <MenuItem value="Dr."sx={{fontFamily : fontFamilyall}}>Dr.</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4.5}>
                <TextField
                  fullWidth
                  sx={{fontFamily:fontFamilyall}}
                  label={
                    <>
                      First Name <span style={{ colorzing: "red" }}>*</span>
                    </>
                  }
                  name="firstname"
                  placeholder="Enter first name"
                  value={formData.firstname}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4.5}>
                <TextField
                  fullWidth
                
                  label={
                   
                    <>
                      Last Name <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  name="lastname"
                  placeholder="Enter last name"
                  value={formData.lastname}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
            </Grid>

            {useEmail ? (
              <TextField
         autoComplete="new-password"

                size="small"
                fullWidth
                label={
                  <>
                    Email <span style={{ color: "red" }}>*</span>
                  </>
                }
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                margin="dense"
              />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <FormControl size="small"  sx={{ minWidth: 100 }}>
                  <InputLabel>
                    Code <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <Select
                    value={selectedCode}
                    onChange={handleCodeChange}
                    label={
                      <>
                        Code <span style={{ color: "red" }}>*</span>
                      </>
                    }
                  >
                    {countryCodes.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label={
                    <>
                      Mobile Number <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  type="tel"
                  name="mobile"
                  placeholder="Enter your mobile number"
                  value={formData.mobile}
                  onChange={handleChange}
                  size="small"
                />
              </Stack>
            )}

            <TextField
         autoComplete="new-password"

              size="small"
              fullWidth
              label={
                <>
                  Password <span style={{ color: "red" }}>*</span>
                </>
              }
              type="password"
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              margin="dense"
            />

            <Typography
              variant="h6"
              fontWeight="bold"
              mt={1}
              mb={1}
              sx={{ color: primaryColor, textAlign: "left", fontFamily:fontFamilyall }}
            >
              Billing Address
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Address Line One <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  name="addresslineone"
                  placeholder="Enter address line one"
                  value={formData.addresslineone}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line Two"
                  name="addresslinetwo"
                  placeholder="Enter address line two (optional)"
                  value={formData.addresslinetwo}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={
                    <>
                      City <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  name="city"
                  placeholder="Enter city"
                  value={formData.city}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Province/State"
                  name="province"
                  placeholder="Enter province/state (optional)"
                  value={formData.province}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Country <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  name="country"
                  placeholder="Enter country"
                  value={formData.country}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={
                    <>
                      Postal Code <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  name="postalcode"
                  placeholder="Enter postal code"
                  value={formData.postalcode}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={1} mb={1}>
              <Typography
                variant="body2"
                sx={{
                  cursor: "pointer",
                  color: primaryColor,
                  "&:hover": { textDecoration: "underline" },
                  fontFamily:fontFamilyall
                }}
                onClick={() => setUseEmail(!useEmail)}
              >
                {useEmail ? "Use Mobile Number" : "Use Email"}
              </Typography>
            </Box>

            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <Link
                style={{ color: primaryColor, fontFamily: fontFamilyall }}
                className="MuiLink-root MuiLink-colorPrimary MuiLink-inherit MuiLink-underlineHover css-17ypemf-JoyLink-root"
                onClick={handleForgotPasswordClick}
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <Typography color="error" sx={{ mb: 1 }}>
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: 1,
                fontFamily:fontFamilyall,
                py: isMobile ? 0.75 : 1,
                borderRadius: 2,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                fontWeight: "bold",
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${darkPrimaryColor} 100%)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${darkPrimaryColor} 0%, ${primaryColor} 100%)`,
                },
                "&.Mui-disabled": {
                  background: "rgba(140, 122, 162, 0.5)",
                  color: "white",
                },
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                "Signup"
              )}
            </Button>
          </form>
        </Box>
      </Modal>

      <ForgotPasswordModal
        isOpen={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
        setToastmessage={setToastmessage}
        setToastColor={setToastColor}
        handleSnackbarClick={handleSnackbarClick}
      />

       <UtilToastMessages
              isOpen={isSnackbarOpen}
              handleClose={() => setSnackbarOpen(false)}
              data={toastmessage}
              severity={toastColor}
            />
    </>
  );
};

export default RegisterPageModal;